import React, { useEffect, useState, useContext, useCallback } from "react";
import InfoCard from "../../utils/components/common/InfoCard/infoCard";
import { UserContext, ReportsDownloadUpdate } from "../../layouts/MainLayout";
import "./ReportsDashboard.scss";
import Icons from "../../utils/components/icons";
import { USER_ROLES } from "../../utils/constants/userConstants";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

const ReportsDashboard = () => {
  const { t } = useTranslation("reports");
  const navigate = useNavigate();
  const userDetails = useContext(UserContext);
  const { reportUpdateStatus } = useContext(ReportsDownloadUpdate);

  const [reportsConfig, setReportConfig] = useState([
    {
      id: 1,
      type: "Device Specific",
      selector: "device_specific",
      options: [
        {
          title: "Device Parameter List",
          selector: "device_parameter_list",
          img: Icons.deviceParameterList,
          visibility: [
            USER_ROLES.viewOnlyUserCorp,
            USER_ROLES.userCorp,
            USER_ROLES.adminCorp,
            USER_ROLES.superAdminCorp,
            USER_ROLES.viewOnlyUserRetail,
            USER_ROLES.userRetail,
            USER_ROLES.adminRetail,
            USER_ROLES.chorusUser,
          ],
          desc: "The Device Parameters List Report includes parameters for a selected Chorus device, helping you to monitor the device and its performance effectively.",
          cta: () => {
            // navigate("/reports/device-parameters", {state: { devicePK: "D#prov_airwolf1097BDDC0A30" }});
            navigate("/reports/device-parameters");
          },
          className: "enabled",
        },
        {
          title: "Device Events",
          selector: "device_event",
          img: Icons.deviceEvents,
          visibility: [
            USER_ROLES.viewOnlyUserCorp,
            USER_ROLES.userCorp,
            USER_ROLES.adminCorp,
            USER_ROLES.superAdminCorp,

            USER_ROLES.viewOnlyUserRetail,
            USER_ROLES.userRetail,
            USER_ROLES.adminRetail,
            USER_ROLES.chorusUser,
          ],
          desc: "The Device Events Report captures,consolidates and provides all the events that are associated to the selected Chorus device.",
          cta: () => {
            // console.log("device Events clicked");
          },
        },
        {
          title: "Device Diagnostics",
          selector: "device_diagnostics",
          img: Icons.deviceDiag,
          className: "enabled",
          visibility: [
            USER_ROLES.viewOnlyUserCorp,
            USER_ROLES.userCorp,
            USER_ROLES.adminCorp,
            USER_ROLES.superAdminCorp,
            USER_ROLES.viewOnlyUserRetail,
            USER_ROLES.userRetail,
            USER_ROLES.adminRetail,
            USER_ROLES.chorusUser,
          ],
          desc: "The Device Diagnostics report is a detailed device log of the selected Chorus device, which helps analyse complete device telemetry, including raw data received from the device sensors.",
          cta: () => {
            navigate("/reports/device-diagnostics");
          },
        },
        {
          title: "Device Summary",
          selector: "device_summary",
          img: Icons.deviceSummary,
          className: "enabled",
          visibility: [
            USER_ROLES.viewOnlyUserCorp,
            USER_ROLES.userCorp,
            USER_ROLES.adminCorp,
            USER_ROLES.superAdminCorp,
            USER_ROLES.viewOnlyUserRetail,
            USER_ROLES.userRetail,
            USER_ROLES.adminRetail,
            USER_ROLES.chorusUser,
          ],
          desc: t("desc.deviceSummary"),
          cta: () => {
            navigate("/reports/summary-details");
          },
        },
      ],
    },
    {
      id: 2,
      type: "Device List",
      selector: "device_list",
      options: [
        {
          title: "Device List",
          selector: "device_list",
          img: Icons.institutionDevice,
          visibility: [
            USER_ROLES.viewOnlyUserCorp,
            USER_ROLES.userCorp,
            USER_ROLES.adminCorp,
            USER_ROLES.viewOnlyUserRetail,
            USER_ROLES.userRetail,
            USER_ROLES.adminRetail,
          ],
          desc: "The Devices List Report lists the current set of Chorus devices at a site level, including device details like, device UDI, name, model, mode, location and cartridge Level",
          cta: () => {
              // navigate("/reports/device-update-list");
          },
        },
        {
          title: t("institutionUpdateDeviceListHeading"),
          selector: "device_update_list",
          img: Icons.institutionDevice,
          className: "enabled",
          visibility: [
            USER_ROLES.viewOnlyUserCorp,
            USER_ROLES.userCorp,
            USER_ROLES.adminCorp,
            USER_ROLES.superAdminCorp
          ],
          desc: "The Devices List Report lists the current set of Chorus devices at a site level, along with OTA related information",
          cta: () => {
            navigate("/reports/device-update-list");
          }
        },
        {
          title: "Institution Device List",
          selector: "institution_device_list",
          img: Icons.institutionDevice,
          visibility: [USER_ROLES.superAdminCorp],
          desc: "The Devices List Report lists the current set of Chorus devices at an institution level, including device details like, site, device UDI, name, model, mode, location and cartridge level",
          cta: () => {
            // console.log("device Diagnostics clicked");
          },
        },
        {
          title: "Institution Device OTA List",
          selector: "device_device_ota_list",
          img: Icons.institutionDevice,
          visibility: [USER_ROLES.superAdminCorp],
          desc: "The Devices List Report lists the current set of Chorus devices at an institution level, along with OTA related information",
          cta: () => {
            // console.log("device Diagnostics clicked");
          },
        },
        {
          title: "Chorus Device List",
          selector: "chorus_device_list",
          img: Icons.institutionDevice,
          visibility: [USER_ROLES.chorusUser],
          desc: "The Devices List Report lists the current set of Chorus devices at a site level, including device details like, device UDI, name, model, mode, location and cartridge Level",
          cta: () => {
            // console.log("device Diagnostics clicked");
          },
        },
        {
          title: "Chorus Device OTA List",
          selector: "chorus_device_ota_list",
          img: Icons.institutionDevice,
          visibility: [USER_ROLES.chorusUser],
          desc: "The Devices List Report lists the current set of Chorus devices at a site level, along with OTA related information",
          cta: () => {
            // console.log("device Diagnostics clicked");
          },
        },
      ],
    },
    {
      id: 3,
      type: "Users",
      selector: "users",
      options: [
        {
          title: "Audit Trail",
          img: Icons.auditTrail,
          visibility: [USER_ROLES.adminCorp, USER_ROLES.superAdminCorp],
          desc: "Audit Trail provides documentary evidence of the sequence of User activities that have affected at any time a specific operation, procedure, event, device or user.",
          cta: () => {
            navigate("/reports/audit-trail");
          },
          className: "enabled",
        },
        {
          title: "Institution User List",
          img: Icons.chorusUser,
          visibility: [USER_ROLES.superAdminCorp],
          desc: "The User List report contains all user related information on the existing users at a institution level, such as, email ID, name, phone no., their roles and other important user details",
          cta: () => {
            // console.log("device Diagnostics clicked");
          },
        },
        {
          title: "Chorus User List",
          img: Icons.chorusUser,
          visibility: [],
          desc: "The User List report contains all user related information on the existing users at a site level, such as, email ID, name, phone no., their roles and other important user details",
          cta: () => {
            // console.log("device Diagnostics clicked");
          },
        },
        {
          title: "User List",
          img: Icons.chorusUser,
          visibility: [
            USER_ROLES.viewOnlyUserRetail,
            USER_ROLES.viewOnlyUserCorp,
            USER_ROLES.userRetail,
            USER_ROLES.userCorp,
            USER_ROLES.adminRetail,
            USER_ROLES.adminCorp,
          ],
          desc: "The User List report contains all user related information on the existing users at a site level, such as, email ID, name, phone no., their roles and other important user details",
          cta: () => {
            // console.log("device Diagnostics clicked");
          },
        },
      ],
    },
    {
      id: 4,
      type: "Institutions",
      selector: "institutions",
      options: [
        {
          title: "Institution List",
          selector: "institution_list",
          img: Icons.institutionuser,
          visibility: [USER_ROLES.chorusUser],
          desc: "The Institution List report contains all Institution related information on the existing institutions such as name, SAAS package, super admin and other important Institution details.",
          cta: () => {
            // console.log("device Diagnostics clicked");
          },
        },
      ],
    },
  ]);

  const gotoDownloads = useCallback(() => {
    navigate("/reports/downloads");
  },[navigate]);

  useEffect(() => {
    const user = userDetails?.loggedInUserRole;

    const updatedReportsConfig = [];
    reportsConfig.forEach((val) => {
      const updatedReportsConfigKeys = [];
      val.options.forEach((option) => {
        if (option.visibility.includes(user)) {
          if (updatedReportsConfig.length) {
            if (updatedReportsConfigKeys.includes(val.id)) {
              const index = updatedReportsConfig.findIndex(
                (el) => el.id === val.id
              );
              updatedReportsConfig[index].options.push(option);
            } else {
              updatedReportsConfig.push({ ...val, options: [option] });
              updatedReportsConfigKeys.push(val.id);
            }
          } else {
            updatedReportsConfig.push({ ...val, options: [option] });
            updatedReportsConfigKeys.push(val.id);
          }
        }
      });
    });
    setReportConfig(updatedReportsConfig);
  }, []);

  return (
    <div className="reportDashboard-container">
      <div className="header-row d-flex justify-content-between fixSubHeader reportSubHeader">
        <div className="header-title">{t("reportsHeading")}</div>
        <div className="d-flex">
          <button
            id="favorites"
            type="button"
            className="roundBtn primary disabled-btn"
            disabled
            title="Not Supported"
          >
            {Icons.starIcon}
            <span>{"Favorites"}</span>
          </button>
          <button
            id="downloads"
            type="button"
            title="Downloads"
            className={
              reportUpdateStatus?.length
                ? "roundBtn primary download-icon"
                : "hide-circle roundBtn primary download-icon"
            }
            onClick={gotoDownloads}
          >
            {Icons.downalodStatusIcon}
            <span>{"Downloads"}</span>
          </button>
        </div>
      </div>
      <div className="datawrapper">
        {reportsConfig.map((ele, ind) => {
          return (
            <div className={`reportsCardList ${ele.selector}`} key={ele.id}>
              {" "}
              <h1 className="top-header">{ele.type}</h1>
              <div className="d-flex flex-wrap justify-content-start">
                {ele.options.map((option, ind) => {
                  return (
                    <div key={option.title} className={`${option.selector}`}>
                      <InfoCard
                        img={option.img}
                        title={option.title}
                        desc={option.desc}
                        clickAction={option.cta}
                        className={option.className}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ReportsDashboard;
